import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from "../contexts/UserContext";
import CRMNav from "./CRMNav";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const getUserId = () => {
  try { const u = localStorage.getItem("user"); return u ? JSON.parse(u).id : null; } catch { return null; }
};

const fmt = (str, withTime = false) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  const date = d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  if (!withTime) return date;
  const time = d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
};

const TIPO_CONFIG = {
  llamada:           { label: "Llamada",          color: "#2563eb", bg: "#eff6ff" },
  visita:            { label: "Visita",            color: "#7c3aed", bg: "#f5f3ff" },
  enviar_cotizacion: { label: "Enviar cotización", color: "#d97706", bg: "#fffbeb" },
  seguimiento:       { label: "Seguimiento",       color: "#059669", bg: "#f0fdf4" },
  administrativo:    { label: "Administrativo",    color: "#6b7280", bg: "#f9fafb" },
  otro:              { label: "Otro",              color: "#6b7280", bg: "#f9fafb" },
};

const PRIORIDAD_CONFIG = {
  alta:  { label: "Alta",  color: "#dc2626", bg: "#fef2f2" },
  media: { label: "Media", color: "#d97706", bg: "#fffbeb" },
  baja:  { label: "Baja",  color: "#6b7280", bg: "#f9fafb" },
};

const EMPTY_FORM = {
  id: "", titulo: "", descripcion: "",
  usuario_id: "", cliente_id: "", contacto_id: "",
  orden_id: "",
  tipo: "otro", prioridad: "media", estado: "pendiente",
  vencimiento: "", recordatorio_at: "",
};

const agruparTareas = (tareas) => {
  const now    = new Date();
  const hoy    = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const semana = new Date(hoy); semana.setDate(semana.getDate() + 7);

  const grupos = { vencidas: [], hoy: [], semana: [], proximas: [], completadas: [] };
  tareas.forEach(t => {
    if (t.estado === "hecha" || t.estado === "cancelada") { grupos.completadas.push(t); return; }
    const venc = new Date(t.vencimiento);
    const vencDay = new Date(venc.getFullYear(), venc.getMonth(), venc.getDate());
    if (vencDay < hoy)         grupos.vencidas.push(t);
    else if (vencDay.getTime() === hoy.getTime()) grupos.hoy.push(t);
    else if (vencDay <= semana) grupos.semana.push(t);
    else                        grupos.proximas.push(t);
  });
  return grupos;
};

// ─── Modal crear / editar tarea ───────────────────────────────────────────────
const TareaModal = ({ initial, clientes, usuarios, onClose, onSaved }) => {
  const [form, setForm]       = useState(initial || EMPTY_FORM);
  const [contactos, setContactos] = useState([]);
  const [saving, setSaving]   = useState(false);
  const uid = getUserId();

  useEffect(() => {
    if (!form.usuario_id && uid) setForm(f => ({ ...f, usuario_id: uid }));
  }, []);

  useEffect(() => {
    if (!form.cliente_id) { setContactos([]); return; }
    axios.post(`${BASE}/crm_contactos_listar.php`, new URLSearchParams({ cliente_id: form.cliente_id }))
      .then(r => setContactos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setContactos([]));
  }, [form.cliente_id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim() || !form.vencimiento) { toast.warning("Título y fecha de vencimiento son obligatorios."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      const payload = { ...form, usuario_id: form.usuario_id || uid, creado_por: form.creado_por || uid };
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await axios.post(`${BASE}/tareas_guardar.php`, fd);
      if (res.data?.success) {
        toast.success(form.id ? "Tarea actualizada." : "Tarea creada.");
        onSaved(); onClose();
      } else toast.error(res.data?.mensaje || "Error al guardar.");
    } catch { toast.error("Error de conexión."); }
    finally { setSaving(false); }
  };

  const F = {
    label: { fontSize: 11, fontWeight: 700, color: "#888", display: "block", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" },
    input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 14, fontFamily: "inherit" },
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 540, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
        <div style={{ background: "#1d4ed8", padding: "18px 24px", borderRadius: "16px 16px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{form.id ? "Editar tarea" : "Nueva tarea"}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <label style={F.label}>Título *</label>
          <input style={F.input} value={form.titulo} onChange={set("titulo")} placeholder="¿Qué hay que hacer?" required />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={F.label}>Tipo</label>
              <select style={F.input} value={form.tipo} onChange={set("tipo")}>
                {Object.entries(TIPO_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={F.label}>Prioridad</label>
              <select style={F.input} value={form.prioridad} onChange={set("prioridad")}>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={F.label}>Vencimiento *</label>
              <input style={F.input} type="datetime-local" value={form.vencimiento} onChange={set("vencimiento")} required />
            </div>
            <div>
              <label style={F.label}>Responsable</label>
              <select style={F.input} value={form.usuario_id} onChange={set("usuario_id")}>
                <option value="">Seleccioná…</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={F.label}>Cliente</label>
              <select style={F.input} value={form.cliente_id} onChange={(e) => setForm(f => ({ ...f, cliente_id: e.target.value, contacto_id: "" }))}>
                <option value="">Sin cliente</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.tercero || c.cliente}</option>)}
              </select>
            </div>
            <div>
              <label style={F.label}>Contacto</label>
              <select style={F.input} value={form.contacto_id} onChange={set("contacto_id")} disabled={!form.cliente_id || contactos.length === 0}>
                <option value="">Sin contacto</option>
                {contactos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          </div>

          {form.id && (
            <div>
              <label style={F.label}>Estado</label>
              <select style={F.input} value={form.estado} onChange={set("estado")}>
                <option value="pendiente">Pendiente</option>
                <option value="en_curso">En curso</option>
                <option value="hecha">Hecha</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          )}

          <label style={F.label}>Descripción / notas</label>
          <textarea style={{ ...F.input, resize: "vertical", minHeight: 60 }} value={form.descripcion} onChange={set("descripcion")} placeholder="Contexto adicional (opcional)…" />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose}
              style={{ padding: "9px 20px", border: "1.5px solid #ccc", borderRadius: 8, background: "none", fontSize: 14, cursor: "pointer", color: "#555" }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: "9px 22px", background: "#1d4ed8", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Guardando…" : form.id ? "Guardar cambios" : "Crear tarea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Fila de tarea ────────────────────────────────────────────────────────────
const TareaRow = ({ tarea, onEdit, onCompletar }) => {
  const tipo = TIPO_CONFIG[tarea.tipo] || TIPO_CONFIG.otro;
  const prio = PRIORIDAD_CONFIG[tarea.prioridad] || PRIORIDAD_CONFIG.media;
  const horas = parseInt(tarea.horas_para_vencer);
  const vencida = horas < 0 && tarea.estado !== "hecha";
  const urgente = horas >= 0 && horas <= 24 && tarea.estado !== "hecha";
  const hecha  = tarea.estado === "hecha" || tarea.estado === "cancelada";

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px",
      background: hecha ? "#fafafa" : vencida ? "#fef9f9" : "#fff",
      borderBottom: "1px solid #f5f0f3",
      opacity: hecha ? 0.65 : 1,
    }}>
      {/* Checkbox */}
      <button onClick={() => !hecha && onCompletar(tarea)}
        style={{
          width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 1,
          border: hecha ? "none" : `2px solid ${vencida ? "#dc2626" : "#d1d5db"}`,
          background: hecha ? "#059669" : "transparent",
          cursor: hecha ? "default" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 11,
        }}>
        {hecha ? "✓" : ""}
      </button>

      {/* Contenido */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: hecha ? "#888" : "#121212", textDecoration: hecha ? "line-through" : "none" }}>
            {tarea.titulo}
          </span>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: tipo.bg, color: tipo.color, fontWeight: 600 }}>{tipo.label}</span>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: prio.bg, color: prio.color, fontWeight: 600 }}>{prio.label}</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
          {tarea.cliente_nombre && (
            <span style={{ fontSize: 12, color: "#888" }}>🏢 {tarea.cliente_nombre}</span>
          )}
          <span style={{ fontSize: 12, color: vencida ? "#dc2626" : urgente ? "#d97706" : "#888", fontWeight: vencida || urgente ? 700 : 400 }}>
            {vencida ? `⚠️ Venció ${fmt(tarea.vencimiento, true)}` : urgente ? `⏰ Vence hoy ${fmt(tarea.vencimiento, true)}` : `📅 ${fmt(tarea.vencimiento, true)}`}
          </span>
          {tarea.usuario_nombre && (
            <span style={{ fontSize: 12, color: "#aaa" }}>👤 {tarea.usuario_nombre}</span>
          )}
        </div>

        {tarea.descripcion && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 4, fontStyle: "italic" }}>
            {tarea.descripcion.slice(0, 100)}{tarea.descripcion.length > 100 ? "…" : ""}
          </div>
        )}
      </div>

      {/* Acciones */}
      {!hecha && (
        <button onClick={() => onEdit(tarea)}
          style={{ background: "none", border: "1.5px solid #e0d6dc", borderRadius: 7, padding: "5px 10px", fontSize: 12, cursor: "pointer", color: "#888", flexShrink: 0 }}>
          ✏️
        </button>
      )}
    </div>
  );
};

// ─── Grupo de tareas ──────────────────────────────────────────────────────────
const GrupoTareas = ({ titulo, tareas, color, onEdit, onCompletar, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (tareas.length === 0) return null;
  return (
    <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 16, border: `1.5px solid ${color}30` }}>
      <div onClick={() => setOpen(v => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer", background: `${color}08`, borderBottom: open ? `1px solid ${color}20` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>{titulo}</span>
          <span style={{ fontSize: 12, padding: "1px 8px", borderRadius: 20, background: color, color: "#fff", fontWeight: 700 }}>{tareas.length}</span>
        </div>
        <span style={{ fontSize: 12, color: "#aaa" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && tareas.map(t => (
        <TareaRow key={t.id} tarea={t} onEdit={onEdit} onCompletar={onCompletar} />
      ))}
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const CRMTareas = () => {
  const { user } = useContext(UserContext);
  const [tareas,       setTareas]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [clientes,     setClientes]     = useState([]);
  const [usuarios,     setUsuarios]     = useState([]);
  const [showModal,    setShowModal]    = useState(false);
  const [editTarea,    setEditTarea]    = useState(null);
  const [filtroUser,   setFiltroUser]   = useState("");
  const [filtroTipo,   setFiltroTipo]   = useState("");
  const [verCompletas, setVerCompletas] = useState(false);

  const fetch = useCallback(() => {
    setLoading(true);
    const fd = new FormData();
    if (verCompletas) fd.append("estado", "todas");
    axios.post(`${BASE}/tareas_listar.php`, fd)
      .then(r => setTareas(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Error al cargar tareas."))
      .finally(() => setLoading(false));
  }, [verCompletas]);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    axios.post(`${BASE}/buscar_terceros.php`)
      .then(r => setClientes((r.data || []).filter(x => x.tipo_other === 2)))
      .catch(() => {});
    axios.post(`${BASE}/buscar_usuarios.php`)
      .then(r => setUsuarios(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const completar = async (tarea) => {
    const fd = new FormData();
    Object.entries({ ...tarea, estado: "hecha", contacto_id: tarea.contacto_id || "", orden_id: tarea.orden_id || "", recordatorio_at: tarea.recordatorio_at || "", descripcion: tarea.descripcion || "" })
      .forEach(([k, v]) => fd.append(k, v ?? ""));
    try {
      const res = await axios.post(`${BASE}/tareas_guardar.php`, fd);
      if (res.data?.success) { toast.success("Tarea completada ✓"); fetch(); }
      else toast.error("Error al actualizar.");
    } catch { toast.error("Error de conexión."); }
  };

  const filtered = tareas.filter(t => {
    if (filtroUser && String(t.usuario_id) !== filtroUser) return false;
    if (filtroTipo && t.tipo !== filtroTipo) return false;
    return true;
  });

  const grupos = agruparTareas(filtered);
  const pendientes = filtered.filter(t => t.estado === "pendiente" || t.estado === "en_curso").length;
  const vencidas   = filtered.filter(t => {
    if (t.estado === "hecha" || t.estado === "cancelada") return false;
    return parseInt(t.horas_para_vencer) < 0;
  }).length;

  return (
    <section style={{ minHeight: "100vh", background: "#f7f4f6", padding: "32px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        <CRMNav />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 }}>Agenda de tareas</h2>
            <p style={{ fontSize: 13, color: "#888", marginTop: 3, marginBottom: 0 }}>
              {pendientes} pendientes
              {vencidas > 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}> · {vencidas} vencidas</span>}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)}
              style={{ padding: "7px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, background: "#fff", outline: "none" }}>
              <option value="">Todos</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
            <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
              style={{ padding: "7px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, background: "#fff", outline: "none" }}>
              <option value="">Todos los tipos</option>
              {Object.entries(TIPO_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={() => setVerCompletas(v => !v)}
              style={{ padding: "7px 14px", border: `1.5px solid ${verCompletas ? "#1d4ed8" : "#e0d6dc"}`, borderRadius: 8, fontSize: 13, background: verCompletas ? "#1d4ed8" : "#fff", color: verCompletas ? "#fff" : "#555", cursor: "pointer" }}>
              {verCompletas ? "Ocultando completadas" : "Ver completadas"}
            </button>
            <button onClick={() => { setEditTarea(null); setShowModal(true); }}
              style={{ padding: "9px 20px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              + Nueva tarea
            </button>
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", color: "#aaa", padding: 60 }}>Cargando tareas…</p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Sin tareas pendientes</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>¡Todo al día!</div>
          </div>
        ) : (
          <>
            <GrupoTareas titulo="Vencidas" tareas={grupos.vencidas} color="#dc2626" onEdit={t => { setEditTarea(t); setShowModal(true); }} onCompletar={completar} />
            <GrupoTareas titulo="Hoy" tareas={grupos.hoy} color="#d97706" onEdit={t => { setEditTarea(t); setShowModal(true); }} onCompletar={completar} />
            <GrupoTareas titulo="Esta semana" tareas={grupos.semana} color="#2563eb" onEdit={t => { setEditTarea(t); setShowModal(true); }} onCompletar={completar} />
            <GrupoTareas titulo="Próximas" tareas={grupos.proximas} color="#6b7280" onEdit={t => { setEditTarea(t); setShowModal(true); }} onCompletar={completar} defaultOpen={false} />
            {verCompletas && <GrupoTareas titulo="Completadas / Canceladas" tareas={grupos.completadas} color="#059669" onEdit={t => { setEditTarea(t); setShowModal(true); }} onCompletar={completar} defaultOpen={false} />}
          </>
        )}
      </div>

      {showModal && (
        <TareaModal
          initial={editTarea ? { ...editTarea, vencimiento: editTarea.vencimiento?.slice(0, 16) || "", recordatorio_at: editTarea.recordatorio_at?.slice(0, 16) || "" } : { ...EMPTY_FORM, usuario_id: getUserId() || "" }}
          clientes={clientes}
          usuarios={usuarios}
          onClose={() => setShowModal(false)}
          onSaved={fetch}
        />
      )}
    </section>
  );
};

export default CRMTareas;
